import {Suspense, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Blankslate} from '@primer/react/experimental'
import {Button, RelativeTime} from '@primer/react'
import {CheckIcon, ChevronDownIcon, ChevronUpIcon} from '@primer/octicons-react'
import {CollapsiblePanel} from './CollapsiblePanel'
import {ExpandableContent} from './ExpandableContent'
import {useActivityQuery} from './data/route-queries'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import type {ActivityPayload} from '../../routes/route-payload-types'
import {buildEventGroups} from '../../helpers/catch-up-helpers'
import {EventList} from './ActivityEventList'
import styles from './Activity.module.css'
import type {NavigationUrls} from '../../types/navigation-urls-types'
import {Link} from '@github-ui/react-core/link'
import {ClientOnly} from '../ClientOnly'
import {SinceSource} from '../../routes/route-payload-types'

const sinceSourceTextMap = {
  [SinceSource.Approved]: 'you approved this pull request',
  [SinceSource.Author]: 'you created this pull request',
  [SinceSource.ChangesRequested]: 'you requested changes on this pull request',
  [SinceSource.Commented]: 'you commented on this pull request',
  [SinceSource.LastSeen]: 'you last viewed this pull request',
}

/** Activity section showing recent timeline updates since last engagement */
export function Activity() {
  return (
    <CollapsiblePanel heading="Activity">
      <ErrorBoundary boundaryName="Activity" fallback={null}>
        <Suspense fallback={null}>
          <ClientOnly>
            <ActivityContent />
          </ClientOnly>
        </Suspense>
      </ErrorBoundary>
    </CollapsiblePanel>
  )
}

function ActivityContent() {
  const {data: activityData} = useActivityQuery()
  const {data: layoutData} = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')

  return <ActivityComponent activityData={activityData} urls={layoutData.urls} />
}

function ActivityComponent({activityData, urls}: {activityData: ActivityPayload; urls: NavigationUrls}) {
  const {changes: changesUrl, conversation: conversationUrl} = urls
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())

  // Show empty state if there's no timestamp or no new items
  if (!activityData.sinceTimestamp || activityData.summary.total === 0) {
    return (
      <ExpandableContent sectionLabel="Activity">
        <Blankslate>
          <Blankslate.Visual>
            <CheckIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading>You&apos;re all caught up</Blankslate.Heading>
          <Blankslate.Description>
            {!activityData.sinceTimestamp
              ? "We'll show new activity here once you interact with this pull request."
              : 'No new activity since your last engagement.'}
          </Blankslate.Description>
        </Blankslate>
      </ExpandableContent>
    )
  }

  const summary = activityData.summary
  const items = buildEventGroups(summary, activityData.sinceTimestamp, changesUrl)

  const toggleGroup = (type: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }

  const sinceSourceText = `Since ${activityData.sinceSource ? (sinceSourceTextMap[activityData.sinceSource] ?? '') : ''}`

  return (
    <ExpandableContent sectionLabel="Activity" maxHeight={1000}>
      <div className={styles.container}>
        <p className={styles.timestamp}>
          {sinceSourceText} <RelativeTime date={new Date(activityData.sinceTimestamp)} />
        </p>
        <ul className={styles.list}>
          {items.map(item => {
            const isExpanded = expandedGroups.has(item.type)
            return (
              <li key={`catch-up-item-${item.type}`} className={styles.item}>
                <div className={styles.itemHeader}>
                  <span className={styles.label}>{item.label}</span>
                  <div className={styles.itemActions}>
                    {item.link && (
                      <Button
                        as={Link}
                        to={item.link}
                        size="small"
                        variant="invisible"
                        aria-label={`View ${item.label.toLowerCase()} in Files tab`}
                      >
                        View changes
                      </Button>
                    )}
                    {item.count > 0 && (
                      <Button
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${item.label.toLocaleLowerCase()}`}
                        size="small"
                        variant="invisible"
                        onClick={() => toggleGroup(item.type)}
                        trailingVisual={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                      />
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <EventList
                    events={item.events}
                    type={item.type}
                    changesUrl={changesUrl}
                    conversationUrl={conversationUrl}
                  />
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </ExpandableContent>
  )
}
